import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

/**
 * Content Mastery Breakdown Component
 * Shows performance by musicals, songs, and lesson sections
 */
export function ContentMasteryBreakdown({ lessons }) {
  const { musicalStats, lessonSectionStats, completionRate } = useMemo(() => {
    if (!lessons || lessons?.length === 0) {
      return { musicalStats: [], lessonSectionStats: [], completionRate: 0 };
    }

    // Aggregate by musical
    const musicalMap = {};
    const sectionMap = {};
    let completedCount = 0;
    let totalCount = lessons?.length;

    lessons?.forEach(lesson => {
      // Musical stats
      const musical = lesson?.musicalTitle || 'Unknown Musical';
      if (!musicalMap?.[musical]) {
        musicalMap[musical] = {
          name: musical,
          completed: 0,
          total: 0,
          avgQuizScore: 0,
          quizCount: 0
        };
      }
      musicalMap[musical].total++;
      if (lesson?.lessonStatus === 'completed') {
        musicalMap[musical].completed++;
        completedCount++;
      }
      if (lesson?.quizCompleted && lesson?.quizScore > 0) {
        musicalMap[musical].avgQuizScore += lesson?.quizScore;
        musicalMap[musical].quizCount++;
      }

      // Lesson section stats
      const section = lesson?.lessonSection || 'unknown';
      if (!sectionMap?.[section]) {
        sectionMap[section] = {
          name: section,
          completed: 0,
          total: 0,
          perfectQuizzes: 0
        };
      }
      sectionMap[section].total++;
      if (lesson?.lessonStatus === 'completed') {
        sectionMap[section].completed++;
      }
      if (lesson?.isQuizPerfect) {
        sectionMap[section].perfectQuizzes++;
      }
    });

    // Calculate averages
    Object.values(musicalMap)?.forEach(musical => {
      if (musical?.quizCount > 0) {
        musical.avgQuizScore = Math.round(musical?.avgQuizScore / musical?.quizCount);
      }
      musical.completionRate = Math.round((musical?.completed / musical?.total) * 100);
    });

    Object.values(sectionMap)?.forEach(section => {
      section.completionRate = Math.round((section?.completed / section?.total) * 100);
    });

    return {
      musicalStats: Object.values(musicalMap)?.sort((a, b) => b?.total - a?.total)?.slice(0, 5),
      lessonSectionStats: Object.values(sectionMap),
      completionRate: Math.round((completedCount / totalCount) * 100)
    };
  }, [lessons]);

  const COLORS = ['#FF6B9D', '#00D4FF', '#FFD700', '#9D4EDD', '#06FFA5', '#FF4D6D'];

  if (!lessons || lessons?.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        No lesson data available yet. Start learning to see your mastery breakdown!
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Completion Rate */}
      <div className="text-center">
        <div className="text-4xl font-bold text-purple-400">{completionRate}%</div>
        <div className="text-sm text-gray-400">Overall Completion Rate</div>
      </div>
      {/* Mastery by Musical */}
      <div>
        <h4 className="text-lg font-semibold mb-4 text-white">Performance by Musical</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={musicalStats}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="name" 
              stroke="rgba(255,255,255,0.6)"
              style={{ fontSize: '11px' }}
              angle={-15}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="rgba(255,255,255,0.6)"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(50, 33, 93, 0.95)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                color: '#fff'
              }}
            />
            <Bar dataKey="completionRate" fill="#00D4FF" name="Completion %" radius={[8, 8, 0, 0]} />
            <Bar dataKey="avgQuizScore" fill="#FFD700" name="Avg Quiz Score" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Lesson Section Distribution */}
      <div>
        <h4 className="text-lg font-semibold mb-4 text-white">Lesson Section Progress</h4>
        <div className="grid grid-cols-2 gap-4">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={lessonSectionStats}
                dataKey="completed"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                labelLine={false}
              >
                {lessonSectionStats?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(50, 33, 93, 0.95)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="space-y-2">
            {lessonSectionStats?.map((section, index) => (
              <div key={section?.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                  />
                  <span className="text-gray-300 capitalize">{section?.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{section?.completionRate}%</div>
                  <div className="text-xs text-gray-400">
                    {section?.completed}/{section?.total} done
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}